/**
 * Magic-link error handling.
 *
 * When a magic link is expired, already-used, or prefetched by an email
 * scanner, Supabase's `/auth/v1/verify` endpoint redirects back to our
 * `emailRedirectTo` (`/account`) with an *error* in the URL rather than
 * tokens, e.g.:
 *
 *   /account#error=access_denied&error_code=otp_expired&error_description=...
 *
 * supabase-js (`detectSessionInUrl`) sees the error, removes any session, and
 * leaves us with no user — so `RequireAuth` bounces to `/login`. Without this
 * module that bounce is silent and looks identical to a broken login.
 *
 * We capture the error params ONCE at app startup (before React Router can
 * strip the hash during its redirect to `/login`), strip them from the address
 * bar so a reload/bookmark can't re-trigger, and hand a friendly message to the
 * LoginPage to display.
 */

export interface AuthError {
  /** Supabase error_code (e.g. 'otp_expired') or error (e.g. 'access_denied'). */
  code: string;
  /** Human-friendly, actionable message to show the user. */
  message: string;
  /** Alert severity — expired links are expected, not scary. */
  type: 'warning' | 'error';
}

const EXPIRED_LINK_MESSAGE =
  'That login link has expired or was already used. Please request a new link below.';

/**
 * Pure parser: extract an auth error from a URL's hash or query string and map
 * it to a friendly message. Returns null when there is no auth error (including
 * the normal success case where the URL carries tokens).
 *
 * Query params take precedence over hash params, mirroring supabase-js's own
 * `parseParametersFromURL`.
 */
export function parseAuthErrorFromUrl(href: string): AuthError | null {
  let url: URL;
  try {
    url = new URL(href, 'http://localhost');
  } catch {
    return null;
  }

  const params: Record<string, string> = {};
  if (url.hash && url.hash[0] === '#') {
    try {
      new URLSearchParams(url.hash.substring(1)).forEach((value, key) => {
        params[key] = value;
      });
    } catch {
      // hash is not a query string — ignore
    }
  }
  url.searchParams.forEach((value, key) => {
    params[key] = value;
  });

  const error = params.error;
  const errorCode = params.error_code;
  const errorDescription = params.error_description;

  if (!error && !errorCode && !errorDescription) {
    return null;
  }

  const code = errorCode || error || 'unknown_error';

  // Expired / consumed one-time links are the common case and are not alarming.
  const isExpiredLink =
    errorCode === 'otp_expired' ||
    error === 'access_denied' ||
    (errorDescription ?? '').toLowerCase().includes('expired') ||
    (errorDescription ?? '').toLowerCase().includes('invalid');

  if (isExpiredLink) {
    return { code, message: EXPIRED_LINK_MESSAGE, type: 'warning' };
  }

  // Anything else: surface the provider's description if we have one.
  const description = errorDescription
    ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
    : 'Something went wrong signing you in. Please request a new login link below.';

  return { code, message: description, type: 'error' };
}

// ─────────────────────────────────────────────────────────────────────────
// One-shot capture, evaluated at module import (before the router mounts).
// ─────────────────────────────────────────────────────────────────────────

let capturedError: AuthError | null = null;
let hasCaptured = false;

/**
 * Snapshot any auth error from the current address bar and strip the error
 * params from the URL. Idempotent — safe to call more than once (e.g. under
 * React StrictMode double-invocation); only the first call reads the URL.
 */
export function captureAuthError(): void {
  if (hasCaptured) return;
  hasCaptured = true;

  if (typeof window === 'undefined') return;

  const error = parseAuthErrorFromUrl(window.location.href);
  if (!error) return;

  capturedError = error;

  // Remove the error params so a reload or shared bookmark can't re-trigger
  // this message. We only touch the URL when we actually found an error, so a
  // real token hash (auth-js's job to clear) is never clobbered.
  try {
    const url = new URL(window.location.href);
    url.hash = '';
    for (const key of ['error', 'error_code', 'error_description']) {
      url.searchParams.delete(key);
    }
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Non-fatal — the message still shows; the URL just keeps its params.
  }
}

/**
 * Non-consuming peek: is there a captured auth error waiting to be shown?
 *
 * Supabase redirects magic-link ERRORS to the project's Site URL *root*
 * (e.g. `https://host/#error=...`), NOT to the `emailRedirectTo` path — so the
 * error can land on `/`, not `/login`. The app uses this to redirect such
 * landings to `/login`, where the message (and "request a new link") belongs.
 */
export function hasAuthError(): boolean {
  return capturedError !== null;
}

/**
 * Return the captured auth error exactly once, then forget it, so it shows on
 * the first LoginPage render but not on subsequent navigations.
 */
export function consumeAuthError(): AuthError | null {
  const error = capturedError;
  capturedError = null;
  return error;
}

/** Test-only: reset module state between test cases. */
export function __resetCapturedAuthErrorForTests(): void {
  capturedError = null;
  hasCaptured = false;
}
